import { z } from 'zod';
import { timesheetsPayrollCurrencyCodeSchema, timesheetsPayrollDateSchema, timesheetsPayrollMoneyCentsSchema, } from './timesheets-payroll-cost-summary.contract.js';
/**
 * Public Timesheets mileage boundary. Distance is stored in thousandths of a mile, the tenant rate
 * is stored in thousandths of a dollar per mile, and reimbursement is rounded once to integer cents.
 * Consumers never read the Timesheets mileage ledger or reconstruct a historical rate.
 */
export const timesheetsMileageCapability = 'timesheets.mileage.v1';
export const TIMESHEETS_MILEAGE_MAX_POINTS_PER_DAY = 2_000;
export const timesheetsMileageDistanceMillimilesSchema = z.number().int().min(0).max(10_000_000);
export const timesheetsMileageAggregateDistanceMillimilesSchema = z.number().int().min(0)
    .max(1_000_000_000_000);
export const timesheetsMileageRateMillsPerMileSchema = z.number().int().min(0).max(1_000_000);
export const timesheetsMileageCoordinateE7Schema = z.number().int();
export const timesheetsMileageLatitudeE7Schema = timesheetsMileageCoordinateE7Schema
    .min(-900_000_000)
    .max(900_000_000);
export const timesheetsMileageLongitudeE7Schema = timesheetsMileageCoordinateE7Schema
    .min(-1_800_000_000)
    .max(1_800_000_000);
export const timesheetsMileageAccuracyMillimetersSchema = z.number().int().min(0).max(1_000_000_000);
export const timesheetsMileageDayStatusSchema = z.enum(['active', 'completed', 'adjusted', 'voided']);
export const timesheetsMileageBasisSourceSchema = z.enum(['calculated', 'adjusted']);
export const timesheetsMileagePointSourceSchema = z.enum([
    'clock_in',
    'location_check',
    'clock_out',
    'geofence',
    'manual',
    'offline_replay',
]);
export const timesheetsMileageEmployeePointSourceSchema = z.enum([
    'location_check',
    'manual',
    'offline_replay',
]);
export const timesheetsMileagePointExclusionSchema = z.enum([
    'single-hop-over-100-miles',
]);
export const timesheetsMileageBasisStatusSchema = z.enum(['available', 'policy-unavailable', 'voided']);
export const timesheetsMileageBasisSnapshotSchema = z.strictObject({
    id: z.string().uuid(),
    basisRevision: z.number().int().positive(),
    policyVersionId: z.string().uuid(),
    policySchemaVersion: z.number().int().positive(),
    policyEffectiveFrom: z.string().datetime({ offset: true }),
    policyChecksum: z.string().regex(/^[0-9a-f]{64}$/),
    currencyCode: timesheetsPayrollCurrencyCodeSchema,
    timeZone: z.string().trim().min(1).max(100),
    source: timesheetsMileageBasisSourceSchema,
    distanceMillimiles: timesheetsMileageDistanceMillimilesSchema,
    rateMillsPerMile: timesheetsMileageRateMillsPerMileSchema,
    reimbursementCents: timesheetsPayrollMoneyCentsSchema,
    capturedByMembershipId: z.string().uuid(),
    capturedAt: z.string().datetime({ offset: true }),
});
export const timesheetsMileageProjectedDaySchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    membershipId: z.string().uuid(),
    workDate: timesheetsPayrollDateSchema,
    personalVehicle: z.boolean(),
    status: timesheetsMileageDayStatusSchema,
    sourceTimeEntryId: z.string().uuid().nullable(),
    startedAt: z.string().datetime({ offset: true }).nullable(),
    endedAt: z.string().datetime({ offset: true }).nullable(),
    pointCount: z.number().int().min(0).max(TIMESHEETS_MILEAGE_MAX_POINTS_PER_DAY),
    calculatedDistanceMillimiles: timesheetsMileageDistanceMillimilesSchema,
    adjustedDistanceMillimiles: timesheetsMileageDistanceMillimilesSchema.nullable(),
    payrollDistanceMillimiles: timesheetsMileageDistanceMillimilesSchema,
    payrollSource: timesheetsMileageBasisSourceSchema.nullable(),
    basisRevision: z.number().int().positive(),
    revision: z.number().int().positive(),
    currentSnapshot: timesheetsMileageBasisSnapshotSchema.nullable(),
    hasHistoricalSnapshot: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
}).superRefine((day, context) => {
    if (!day.personalVehicle || day.status === 'voided') {
        if (day.payrollDistanceMillimiles !== 0 || day.payrollSource !== null || day.currentSnapshot !== null) {
            context.addIssue({
                code: 'custom',
                path: ['status'],
                message: 'Voided or opted-out mileage days cannot expose a payroll basis.',
            });
        }
        return;
    }
    const expectedSource = day.adjustedDistanceMillimiles === null ? 'calculated' : 'adjusted';
    const expectedDistance = day.adjustedDistanceMillimiles ?? day.calculatedDistanceMillimiles;
    if (day.payrollSource !== expectedSource || day.payrollDistanceMillimiles !== expectedDistance) {
        context.addIssue({
            code: 'custom',
            path: ['payrollDistanceMillimiles'],
            message: 'Mileage payroll distance must use adjusted-over-calculated precedence.',
        });
    }
});
export const timesheetsMileagePointSchema = z.strictObject({
    id: z.string().uuid(),
    mileageDayId: z.string().uuid(),
    membershipId: z.string().uuid(),
    timeEntryId: z.string().uuid().nullable(),
    occurredAt: z.string().datetime({ offset: true }),
    latitudeE7: timesheetsMileageLatitudeE7Schema,
    longitudeE7: timesheetsMileageLongitudeE7Schema,
    accuracyMillimeters: timesheetsMileageAccuracyMillimetersSchema.nullable(),
    distanceFromPreviousMillimiles: timesheetsMileageDistanceMillimilesSchema,
    source: timesheetsMileagePointSourceSchema,
    exclusion: timesheetsMileagePointExclusionSchema.nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
export const timesheetsMileageSetOwnDaySchema = z.strictObject({
    commandId: z.string().uuid(),
    workDate: timesheetsPayrollDateSchema,
    personalVehicle: z.boolean(),
    occurredAt: z.string().datetime({ offset: true }),
    sourceTimeEntryId: z.string().uuid().nullable().default(null),
});
export const timesheetsMileageRecordPointSchema = z.strictObject({
    clientPointId: z.string().uuid(),
    workDate: timesheetsPayrollDateSchema,
    timeEntryId: z.string().uuid().nullable().default(null),
    occurredAt: z.string().datetime({ offset: true }),
    latitudeE7: timesheetsMileageLatitudeE7Schema,
    longitudeE7: timesheetsMileageLongitudeE7Schema,
    accuracyMillimeters: timesheetsMileageAccuracyMillimetersSchema.nullable().default(null),
    source: timesheetsMileageEmployeePointSourceSchema,
});
export const timesheetsMileageAdjustDaySchema = z.strictObject({
    commandId: z.string().uuid(),
    membershipId: z.string().uuid(),
    workDate: timesheetsPayrollDateSchema,
    occurredAt: z.string().datetime({ offset: true }),
    expectedRevision: z.number().int().positive().nullable(),
    distanceMillimiles: timesheetsMileageDistanceMillimilesSchema,
    reason: z.string().trim().min(5).max(2_000),
});
export const timesheetsMileageDayResponseSchema = z.strictObject({
    data: timesheetsMileageProjectedDaySchema,
    idempotent: z.boolean().optional(),
    basisStatus: timesheetsMileageBasisStatusSchema.optional(),
});
export const timesheetsMileageOwnDayResponseSchema = z.strictObject({
    data: timesheetsMileageProjectedDaySchema.nullable(),
});
export const timesheetsMileagePointResponseSchema = z.strictObject({
    data: z.strictObject({
        day: timesheetsMileageProjectedDaySchema,
        point: timesheetsMileagePointSchema,
    }),
    idempotent: z.boolean(),
    basisStatus: timesheetsMileageBasisStatusSchema,
});
export const timesheetsMileageSummaryQuerySchema = z.strictObject({
    from: timesheetsPayrollDateSchema,
    to: timesheetsPayrollDateSchema,
    membershipId: z.string().uuid().optional(),
    projectFilterId: z.string().uuid().optional(),
    limit: z.number().int().min(1).max(2_000).default(500),
}).refine((query) => query.to >= query.from, {
    path: ['to'],
    message: 'Mileage range end must not precede its start.',
});
export const timesheetsMileageSummaryIssueSchema = z.strictObject({
    code: z.enum(['missing-current-rate-snapshot', 'stale-rate-snapshot']),
    mileageDayId: z.string().uuid(),
    membershipId: z.string().uuid(),
    workDate: timesheetsPayrollDateSchema,
});
export const timesheetsMileageIncompleteDaySchema = z.strictObject({
    id: z.string().uuid(),
    membershipId: z.string().uuid(),
    workDate: timesheetsPayrollDateSchema,
    status: timesheetsMileageDayStatusSchema,
    pointCount: z.number().int().min(0).max(TIMESHEETS_MILEAGE_MAX_POINTS_PER_DAY),
    calculatedDistanceMillimiles: timesheetsMileageDistanceMillimilesSchema,
    adjustedDistanceMillimiles: timesheetsMileageDistanceMillimilesSchema.nullable(),
    payrollDistanceMillimiles: timesheetsMileageDistanceMillimilesSchema,
    payrollSource: timesheetsMileageBasisSourceSchema,
});
export const timesheetsMileageReadySummarySchema = z.strictObject({
    contractVersion: z.literal(timesheetsMileageCapability),
    status: z.literal('ready'),
    range: z.strictObject({ from: timesheetsPayrollDateSchema, to: timesheetsPayrollDateSchema }),
    data: z.strictObject({
        days: z.array(timesheetsMileageProjectedDaySchema).max(2_000),
        byMembership: z.array(z.strictObject({
            membershipId: z.string().uuid(),
            dayCount: z.number().int().nonnegative(),
            distanceMillimiles: timesheetsMileageAggregateDistanceMillimilesSchema,
            reimbursementCents: timesheetsPayrollMoneyCentsSchema,
        })).max(2_000),
        totals: z.strictObject({
            dayCount: z.number().int().nonnegative(),
            membershipCount: z.number().int().nonnegative(),
            distanceMillimiles: timesheetsMileageAggregateDistanceMillimilesSchema,
            reimbursementCents: timesheetsPayrollMoneyCentsSchema,
        }),
    }),
});
export const timesheetsMileageIncompleteSummarySchema = z.strictObject({
    contractVersion: z.literal(timesheetsMileageCapability),
    status: z.literal('basis-incomplete'),
    range: z.strictObject({ from: timesheetsPayrollDateSchema, to: timesheetsPayrollDateSchema }),
    data: z.null(),
    days: z.array(timesheetsMileageIncompleteDaySchema).max(2_000),
    issues: z.array(timesheetsMileageSummaryIssueSchema).min(1).max(2_000),
});
export const timesheetsMileageProjectFilterExcludedSummarySchema = z.strictObject({
    contractVersion: z.literal(timesheetsMileageCapability),
    status: z.literal('project-filter-excluded'),
    range: z.strictObject({ from: timesheetsPayrollDateSchema, to: timesheetsPayrollDateSchema }),
    projectFilterId: z.string().uuid(),
    reason: z.literal('mileage-is-not-project-attributable'),
    data: z.null(),
});
export const timesheetsMileageSummaryResponseSchema = z.discriminatedUnion('status', [
    timesheetsMileageReadySummarySchema,
    timesheetsMileageIncompleteSummarySchema,
    timesheetsMileageProjectFilterExcludedSummarySchema,
]);
//# sourceMappingURL=timesheets-mileage.contract.js.map